import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import cls from "./ThatPending.module.scss";
import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";
import { useCall } from "@/app/call-context-state";
import chatIcon from "@/assets/UI/chat.webp";
import audioCallIcon from "@/assets/UI/audioCall.webp";
import videoCallIcon from "@/assets/UI/videoCall.webp";
import emailIcon from "@/assets/UI/email.webp";
import myProfileIcon from "@/assets/UI/myProfile.webp";

const ThatPending = () => {
  const navigate = useNavigate();
  const { startCall, activeCall, isConnecting } = useCall();

  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
    total: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchMembers = async (p = 1) => {
    try {
      setLoading(true);
      const res = await api.get(
        `/v1/admin/members/members-that-pending?page=${p}&limit=50`,
      );

      setMembers(res.data.data || []);
      setMeta({
        totalPages: res.data.totalPages,
        hasNextPage: res.data.hasNextPage,
        hasPrevPage: res.data.hasPrevPage,
        total: res.data.total,
      });
      setPage(res.data.page);
    } catch (err) {
      console.error("Error loading members that are pending:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers(1);
  }, []);

  const handleStartCall = async (calleeId, callType) => {
    if (!calleeId) return;
    await startCall({ calleeId, callType });
  };

  const renderAvatar = (m) => {
    if (m.avatar && m.avatar.url) {
      return (
        <div className={cls.avatarWrap}>
          <img
            src={m.avatar.url}
            alt={m.username || "Member avatar"}
            className={cls.avatarImg}
          />
        </div>
      );
    }

    // Fallback: initials from username
    const initial = (m.username && m.username.charAt(0).toUpperCase()) || "?";

    return (
      <div className={`${cls.avatarWrap} ${cls.avatarFallback}`}>
        <span className={cls.avatarInitial}>{initial}</span>
      </div>
    );
  };

  const renderMemberType = (memberType) => {
    const label = memberType === "couple" ? "Couple Member" : "Single Member";

    const typeClass =
      memberType === "couple" ? cls.memberTypeCouple : cls.memberTypeSingle;

    return (
      <span className={`${cls.memberTypeBadge} ${typeClass}`}>{label}</span>
    );
  };

  return (
    <div className={cls.container}>
      <div className={cls.headerRow}>
        <button
          type="button"
          className={cls.backBtn}
          onClick={() => navigate("/admin/members")}
        >
          ← Back to Members
        </button>
        <div className={cls.titleBlock}>
          <h2 className={cls.title}>Members Waiting (Pending)</h2>
          <p className={cls.subtitle}>
            Active members whose profiles are{" "}
            <strong>ready and waiting for approval.</strong>
          </p>
        </div>
      </div>

      <div className={cls.metaBar}>
        <span className={cls.chip}>
          Total pending: <strong>{meta.total}</strong>
        </span>
        <span className={cls.chipSoft}>
          Page {page} / {meta.totalPages || 1}
        </span>
      </div>

      {loading ? (
        <div className={cls.loading}>
          <span className={cls.spinner} />
          <span>Loading pending members...</span>
        </div>
      ) : members.length === 0 ? (
        <div className={cls.empty}>
          <p>No members currently pending approval ✨</p>
          <p className={cls.emptySub}>
            Everyone is either still creating a profile or already approved.
          </p>
        </div>
      ) : (
        <>
          <div className={cls.tableWrap}>
            <table className={cls.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Member</th>

                  <th className={cls.actionsCol}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m, index) => (
                  <tr key={m.id}>
                    <td>{(page - 1) * 50 + index + 1}</td>
                    <td className={cls.memberCell}>
                      {renderAvatar(m)}
                      <div className={cls.memberText}>
                        <div className={cls.username}>@{m.username}</div>
                        <div className={cls.statusHint}>
                          Status: <span>Pending</span> | {timeAgo(m.joinedAt)}
                        </div>
                        <div className={cls.statusHint}>
                          {renderMemberType(m.memberType)}
                        </div>
                      </div>
                    </td>
                    <td className={cls.actionsCell}>
                      {" "}
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() => navigate(`/admin/chat/${m.id}`)}
                        aria-label={`Chat with ${m.username}`}
                      >
                        <img src={chatIcon} alt="" className={cls.chatIcon} />
                      </button>
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() => handleStartCall(m.userId, "audio")}
                        disabled={!m.userId || isConnecting || !!activeCall}
                        aria-label={`Audio call ${m.username}`}
                      >
                        <img
                          src={audioCallIcon}
                          alt=""
                          className={cls.chatIcon}
                        />
                      </button>
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() => handleStartCall(m.userId, "video")}
                        disabled={!m.userId || isConnecting || !!activeCall}
                        aria-label={`Video call ${m.username}`}
                      >
                        <img
                          src={videoCallIcon}
                          alt=""
                          className={cls.chatIcon}
                        />
                      </button>
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() =>
                          navigate(`/admin/members/email/${m.userId}`)
                        }
                        aria-label={`Email ${m.username}`}
                      >
                        <img src={emailIcon} alt="" className={cls.chatIcon} />
                      </button>
                      <button
                        type="button"
                        className={cls.chatBtn}
                        onClick={() =>
                          navigate(`/admin/members/single/${m.userId}`)
                        }
                      >
                        <img
                          src={myProfileIcon}
                          alt=""
                          className={cls.chatIcon}
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={cls.pagination}>
            <button
              type="button"
              disabled={!meta.hasPrevPage || loading}
              onClick={() => fetchMembers(page - 1)}
            >
              ← Prev
            </button>
            <span>
              Page {page} / {meta.totalPages || 1}
            </span>
            <button
              type="button"
              disabled={!meta.hasNextPage || loading}
              onClick={() => fetchMembers(page + 1)}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ThatPending;
